const vnd = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });
const qtyFmt = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 3 });

export const formatMoney = (v: number | null | undefined) => (v === null || v === undefined ? '' : vnd.format(v));
export const formatQty = (v: number | null | undefined) => (v === null || v === undefined ? '' : qtyFmt.format(v));

/** Đọc số tiền bằng chữ tiếng Việt (VND, số nguyên không âm). */
export function moneyToVietnameseWords(amount: number): string {
  const n = Math.round(Math.abs(amount));
  if (n === 0) return 'Không đồng';
  const digits = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  const units = ['', ' nghìn', ' triệu', ' tỷ', ' nghìn tỷ', ' triệu tỷ'];

  const readTriple = (num: number, full: boolean): string => {
    const h = Math.floor(num / 100);
    const t = Math.floor((num % 100) / 10);
    const u = num % 10;
    const parts: string[] = [];
    if (full || h > 0) parts.push(`${digits[h]} trăm`);
    if (t === 0) {
      if (u > 0 && (full || h > 0)) parts.push('linh');
    } else if (t === 1) parts.push('mười');
    else parts.push(`${digits[t]} mươi`);
    if (u > 0) {
      if (u === 1 && t > 1) parts.push('mốt');
      else if (u === 5 && t > 0) parts.push('lăm');
      else parts.push(digits[u]!);
    }
    return parts.join(' ');
  };

  const groups: number[] = [];
  let rest = n;
  while (rest > 0) {
    groups.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  const words: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i]!;
    if (g === 0) continue;
    words.push(readTriple(g, i < groups.length - 1) + units[i]);
  }
  const s = words.join(' ').replace(/\s+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1) + ' đồng';
}

const THOUSANDS = (sep: string) => new RegExp(`^\\d{1,3}(\\${sep}\\d{3})+$`);

/**
 * Đọc số người dùng gõ hoặc dán (từ Excel, email, Zalo). Trả về null nếu không chắc chắn đọc đúng,
 * để ô nhập báo lỗi thay vì lặng lẽ ghi sai số.
 *  - Kiểu VN:  "1.250.000" → 1250000 · "1.250" → 1250 · "125,5" → 125.5 · "1.250,5" → 1250.5
 *  - Kiểu US (dán từ Excel/email tiếng Anh): "1,250,000" → 1250000 · "12,500.50" → 12500.5
 *  - Có cả "." và ",": dấu đứng sau cùng là dấu thập phân.
 *  - Chỉ một dấu ",": theo thói quen VN là dấu thập phân ("1,250" → 1.25).
 *  - Bỏ khoảng trắng, "đ", "₫", "VND".
 */
export function parseNumberVN(input: string): number | null {
  let s = input.replace(/[\s\u00A0]/g, '').replace(/(vnđ|vnd|đ|₫)$/i, '');
  if (!s) return null;
  let sign = '';
  if (s.startsWith('-')) {
    sign = '-';
    s = s.slice(1);
  }
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;

  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let intPart = s;
  let frac = '';
  if (lastDot >= 0 && lastComma >= 0) {
    const dec = lastDot > lastComma ? '.' : ',';
    const thou = dec === '.' ? ',' : '.';
    const at = s.lastIndexOf(dec);
    intPart = s.slice(0, at);
    frac = s.slice(at + 1);
    if (!THOUSANDS(thou).test(intPart) || !/^\d+$/.test(frac)) return null;
    intPart = intPart.split(thou).join('');
  } else if (lastComma >= 0) {
    const parts = s.split(',');
    if (parts.length > 2) {
      if (!THOUSANDS(',').test(s)) return null;
      intPart = parts.join('');
    } else {
      [intPart, frac] = parts as [string, string];
      if (!/^\d+$/.test(intPart) || !/^\d+$/.test(frac)) return null;
    }
  } else if (lastDot >= 0) {
    const parts = s.split('.');
    if (parts.length > 2 || parts[1]!.length === 3) {
      if (!THOUSANDS('.').test(s)) return null;
      intPart = parts.join('');
    } else {
      [intPart, frac] = parts as [string, string];
      if (!/^\d+$/.test(intPart) || !/^\d+$/.test(frac)) return null;
    }
  }
  const n = Number(`${sign}${intPart}${frac ? `.${frac}` : ''}`);
  return Number.isFinite(n) ? n : null;
}
